import { Check, ChevronDown, ShieldCheck, Zap } from 'lucide-react';

import {
    AGENT_APPROVAL_MODE_OPTIONS,
    AGENT_APPROVAL_MODES,
    type AgentApprovalModeControlProps,
} from '@apps/web/components/phoebe_chat/agent_approval_mode_control';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';
import { type AgentApprovalMode } from '@libraries/typescript/api_client';

function V3ApprovalModeIcon({
    mode,
    className,
}: {
    readonly mode: AgentApprovalMode;
    readonly className?: string;
}) {
    const Icon = mode === 'autopilot' ? Zap : ShieldCheck;
    return (
        <Icon
            aria-hidden={true}
            className={cn('size-3.5 shrink-0', className)}
        />
    );
}

export function V3ApprovalModeControl({
    pinnedMode,
    resolvedMode,
    canEdit,
    disabled = false,
    onModeChange,
}: AgentApprovalModeControlProps) {
    const activeOption = AGENT_APPROVAL_MODE_OPTIONS[resolvedMode];
    const accentClass =
        resolvedMode === 'autopilot' ? 'text-[#5533EE]' : 'text-[#617578]';

    if (!canEdit) {
        return (
            <div
                data-testid="v3-approval-mode"
                className={cn(
                    'flex items-center gap-1.5 pt-2 text-xs leading-4 font-medium',
                    accentClass,
                )}
            >
                <V3ApprovalModeIcon mode={resolvedMode} />
                <span>{activeOption.label}</span>
            </div>
        );
    }

    return (
        <div data-testid="v3-approval-mode" className="flex pt-2">
            <DropdownMenu>
                <DropdownMenuTrigger asChild={true}>
                    <button
                        type="button"
                        disabled={disabled}
                        aria-label={`Approval mode: ${activeOption.label}`}
                        className={cn(
                            'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-2 py-1 text-xs leading-4 font-medium transition-colors hover:bg-[#F0F5F5] disabled:pointer-events-none disabled:opacity-50',
                            accentClass,
                        )}
                    >
                        <V3ApprovalModeIcon mode={resolvedMode} />
                        <span className="truncate">{activeOption.label}</span>
                        <ChevronDown className="size-3 shrink-0" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80 p-2">
                    {AGENT_APPROVAL_MODES.map((mode) => (
                        <DropdownMenuItem
                            key={mode}
                            disabled={disabled}
                            className="items-start gap-2 py-2"
                            onClick={
                                disabled ? undefined : () => onModeChange(mode)
                            }
                        >
                            <V3ApprovalModeIcon
                                mode={mode}
                                className="mt-0.5"
                            />
                            <span className="flex flex-1 flex-col gap-0.5">
                                <span className="font-medium">
                                    {AGENT_APPROVAL_MODE_OPTIONS[mode].label}
                                </span>
                                <span className="text-muted-foreground text-xs leading-4">
                                    {
                                        AGENT_APPROVAL_MODE_OPTIONS[mode]
                                            .description
                                    }
                                </span>
                            </span>
                            {(pinnedMode ?? resolvedMode) === mode ? (
                                <Check className="mt-0.5 size-4" />
                            ) : null}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
